import React from 'react';
import { simpleInertiaApp } from '@lifespikes/js-beam';
import { createRoot } from 'react-dom/client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FacebookProvider } from 'react-facebook';
import {
  VITE_FACEBOOK_APP_DEFAULT_GRAPH_VERSION,
  VITE_FACEBOOK_APP_ID,
  VITE_FULLSTORY_ORG,
  VITE_SENTRY_DSN,
} from '@php-beam/packages/frontend/src/config';
import { PusherNotificationClientProvider } from '@php-beam/packages/frontend/src/contexts/PusherContextProvider';
import {
  setupLaravelEcho,
  disconnectEcho,
} from '@php-beam/packages/frontend/src/support/utils';
import * as Sentry from '@sentry/react';
import * as FullStory from '@fullstory/browser';
import Progress from '@php-beam/packages/frontend/src/support/progress';
import { KeyBindProvider } from 'react-keybinds';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-credit-cards/es/styles-compiled.css';
import 'react-datepicker/dist/react-datepicker.min.css';
import './styles/index.css';
import JanusThemeContextProvider from '@php-beam/packages/frontend/src/contexts/JanusThemeContextProvider';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import dayjs from 'dayjs';
// @ts-ignore
import dayjsBusinessDays from 'dayjs-business-days';
import { Inertia } from '@inertiajs/inertia';
import axios from 'axios';
import {
  CLIENT_URL_HEADER,
  currentPageUrl,
  sendClientUrl,
} from '@php-beam/packages/frontend/src/api/client';

if (VITE_FULLSTORY_ORG) {
  FullStory.init({ orgId: VITE_FULLSTORY_ORG });
}

if (VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: VITE_SENTRY_DSN,
    integrations: [new Sentry.BrowserTracing()],
    tracesSampleRate: 0.2,
  });
}

if (typeof window !== 'undefined') {
  window.global = window;

  let stale = false;

  window.addEventListener('popstate', () => {
    stale = true;
  });

  // Inertia 0.11 sends its visits through its own copy of axios, which the axios interceptor
  // below does not reach, so the page URL is added to each visit here. The headers object is
  // the one the request is built from, so it is changed in place.
  Inertia.on('before', (event) => {
    const { visit } = event.detail;

    if (visit.url.origin === window.location.origin) {
      visit.headers[CLIENT_URL_HEADER] = currentPageUrl();
    }
  });

  Inertia.on('navigate', (event) => {
    const page = event.detail.page;

    if (stale) {
      Inertia.get(page.url, {}, { replace: true, preserveState: false });
    }
    stale = false;
  });
}

Progress.init({
  showSpinner: true,
});

setupLaravelEcho();

axios.interceptors.request.use(sendClientUrl);

// Setup axios interceptor to disconnect Echo when broadcasting auth fails
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only disconnect Echo if the broadcasting auth endpoint specifically fails
    if (
      error.config?.url?.includes('/broadcasting/auth') &&
      (error.response?.status === 401 || error.response?.status === 403)
    ) {
      disconnectEcho();
    }
    return Promise.reject(error);
  },
);

dayjs.extend(dayjsBusinessDays);

dayjs.extend(advancedFormat);

const queryClient = new QueryClient();

void simpleInertiaApp({
  pages: import.meta.glob('./pages/**/*.{tsx,jsx}'),
  setup({ el, App, props: setupProps }) {
    const root = createRoot(el);
    root.render(
      <PusherNotificationClientProvider>
        <KeyBindProvider>
          <QueryClientProvider client={queryClient}>
            <App {...setupProps}>
              {({ Component, props, key }) => (
                <JanusThemeContextProvider>
                  <FacebookProvider
                    appId={VITE_FACEBOOK_APP_ID}
                    version={VITE_FACEBOOK_APP_DEFAULT_GRAPH_VERSION}
                  >
                    <Component {...props} key={key} />
                  </FacebookProvider>
                </JanusThemeContextProvider>
              )}
            </App>
            <ReactQueryDevtools initialIsOpen={false} position={'top-left'} />
          </QueryClientProvider>
        </KeyBindProvider>
      </PusherNotificationClientProvider>,
    );
  },
});
